











































































































import { Prop, Component, Emit, Vue } from 'vue-property-decorator';
import type { IField, IMergeValues, TextInputSettings } from '@getaccept/editor-lib';
import { EContext, FieldInputType, ProsemirrorHelper, EditorHelper } from '@getaccept/editor-lib';
import type { IUser } from '@lib-shared/users/types/user';
import type { IRecipient } from '@lib-shared/types/Recipient';
import type { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';
import { SigningHelper } from '@lib-shared/signature/helpers/signing.helper';
import type { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { ETooltipPlacement } from '@lib-shared/enums/tooltip-placement';
import type { ITooltip } from '@lib-shared/types/tooltip';
import InputDropdown from './InputDropdown.vue';
import ProsemirrorViewerFormOption from './ProsemirrorViewerFormOption.vue';
import ProsemirrorViewerFormInput from './ProsemirrorViewerFormInput.vue';
import InputCheckbox from './InputCheckbox.vue';

@Component({
  components: {
    InputCheckbox,
    InputDropdown,
    ProsemirrorViewerFormInput,
    ProsemirrorViewerFormOption,
  },
  data: () => ({
    EditorHelper,
    FieldInputType,
    ProsemirrorHelper,
  }),
})
export default class InputSet extends Vue {
  @Prop() inputSet: IField[];
  @Prop({ default: () => EContext.Document }) context: EContext;
  @Prop() fields: IField[];
  @Prop() focusedRequiredId?: string;
  @Prop() mergeValues: IMergeValues;
  @Prop() isSigningSite: boolean;
  @Prop() sender: IUser;
  @Prop() recipient: IRecipient;
  @Prop() recipientInput!: IEditorRecipientInput[];
  @Prop() isMobile: boolean;
  @Prop() isPdfViewer: boolean;
  @Prop({ default: () => [] }) updatingInputs: IEditorRecipientInput[];
  @Prop({ default: () => [] }) failedInputUpdates: IEditorRecipientInput[];

  @Emit('update-value') updateValue(payload: { value: string; inputId: string }) {
    return payload;
  }

  get hasRecipientSigned(): boolean {
    return SigningHelper.hasRecipientSigned(this.recipient?.status as ERecipientStatus);
  }

  hasUpdateError(inputId: string): boolean {
    return this.failedInputUpdates.some(input => input.fieldId === inputId);
  }

  isUpdatingInputValue(inputId: string): boolean {
    return this.updatingInputs.some(input => input.fieldId === inputId);
  }

  hasFocus(input: IField) {
    if (!this.getIsAssignedToCurrentUser(input)) {
      return false;
    }
    return input.id === this.focusedRequiredId;
  }

  getIsEditable(input: IField) {
    return (
      this.getIsAssignedToCurrentUser(input) &&
      !this.hasRecipientSigned &&
      this.context !== EContext.DocumentView
    );
  }

  getIsSigningSiteAndAssignedToRecipient(recipientId: string) {
    return this.isSigningSite && this.recipient?.id === recipientId;
  }

  getIsAssignedToCurrentUser(input: IField) {
    return (
      this.getIsSigningSiteAndAssignedToRecipient(input.recipientId) ||
      this.getIsAppSiteAndAssignedToSender(input.userId)
    );
  }

  getAssignedRecipient(recipientId: string): IRecipient {
    return this.mergeValues?.recipients?.find(recipient => recipient.id === recipientId);
  }

  getTooltip(input: IField): ITooltip {
    return {
      content: this.getIsAssignedToCurrentUser(input)
        ? this.$t('field-assigned-to-you').toString()
        : `${this.$t('field-assigned-to').toString()} ${this.getAssigneeFullName(input)}`,
      offset: 10,
      placement: ETooltipPlacement.Top,
    };
  }

  getAssigneeFullName(input: IField): string {
    const fullName =
      this.sender?.id === input.userId
        ? this.sender?.fullName
        : this.getAssignedRecipient(input.recipientId)?.fullName;
    return fullName || '';
  }

  getIsAppSiteAndAssignedToSender(userId: string) {
    return !this.isSigningSite && this.sender?.id === userId;
  }

  getInputStyle(input: IField): string {
    const width = this.isMobile ? 100 : input.inputSettings?.width || 100;
    return `width: ${width}%;`;
  }

  handleInputUpdate(value: string, input: IField & { inputSettings: TextInputSettings }) {
    if (EditorHelper.getTextValue(input, this.recipientInput) === value) {
      return;
    }
    this.updateValue({ value, inputId: input.id });
  }

  handleCheckboxUpdate(value: boolean, inputId: string) {
    this.updateValue({ value: value ? '1' : '0', inputId });
  }
}
